import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useAuthStore } from '@/stores/auth'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'

// Pages
import { HomePage } from '@/pages/Home'
import { LoginPage } from '@/pages/Login'
import { SignupPage } from '@/pages/Signup'
import { ForgotPasswordPage } from '@/pages/ForgotPassword'
import { ResetPasswordPage } from '@/pages/ResetPassword'
import { DashboardPage } from '@/pages/Dashboard'
import { LinksPage } from '@/pages/Links'
import { AnalyticsPage } from '@/pages/Analytics'
import { AccountPage } from '@/pages/Account'
import { AuthCallbackPage } from '@/pages/AuthCallback'
import { WelcomePage } from '@/pages/Welcome'
import { PricingPage } from '@/pages/Pricing'
import { BioPages } from '@/pages/BioPages'
import { BioPageEditor } from '@/pages/BioPageEditor'
import { BioPageWizard } from '@/pages/BioPageWizard'
import { Instagram } from '@/pages/Instagram'
import { InstagramRuleEditor } from '@/pages/InstagramRuleEditor'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicy'
import { FeedbackPage } from '@/pages/Feedback'
import { PageErrorBoundary } from '@/components/PageErrorBoundary'

function App() {
  const { initialize, initialized } = useAuthStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <Sentry.ErrorBoundary fallback={() => (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-gray-500 mb-6">An unexpected error occurred. Please refresh the page to try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition"
          >
            Refresh Page
          </button>
        </div>
      </div>
    )}>
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Dashboard">
                  <DashboardPage />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/links"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Links">
                  <LinksPage />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Analytics">
                  <AnalyticsPage />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Account">
                  <AccountPage />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bio-pages"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Bio Pages">
                  <BioPages />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bio-pages/new"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Create Bio Page">
                  <BioPageWizard />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bio-pages/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Bio Page Editor">
                  <BioPageEditor />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/instagram"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Instagram">
                  <Instagram />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/instagram/rules/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <PageErrorBoundary pageName="Instagram Rule Editor">
                  <InstagramRuleEditor />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feedback"
          element={
            <ProtectedRoute>
              <Layout title="Feedback & Support">
                <PageErrorBoundary pageName="Feedback">
                  <FeedbackPage />
                </PageErrorBoundary>
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}

export default App
